import { CheckmarkAltIcon } from '@kijiji/icons/src/icons'
import { colors } from '@kijiji/theme'
import { useTranslation } from 'next-i18next'
import { type FC, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { VipSectionTitle } from '@/components/vip/shared/vip-section-title/VipSectionTitle'
import { CPOProgramLogo, DetailList } from '@/features/certified-pre-owned/components/styled'
import { cpoProgramDetailKeys } from '@/features/certified-pre-owned/constants/programDetails'
import { BodyText } from '@/ui/atoms/body-text'
import { Flex } from '@/ui/atoms/flex'
import { ListItem } from '@/ui/atoms/list-item'
import { Spacing } from '@/ui/atoms/spacing'

export type CertifiedPreOwnedProps = {
  partnerId: string
}

export const CertifiedPreOwned: FC<CertifiedPreOwnedProps> = ({ partnerId }) => {
  const { spacing } = useTheme()
  const { t } = useTranslation('cpo')

  const isPartnerRecognized = useMemo(
    // If the partnerId is not recognized, number of bullets is undefined
    () => !!cpoProgramDetailKeys[partnerId],
    [partnerId]
  )

  return (
    <Spacing mTop={spacing.xLarge} mBottom={spacing.default} data-testid="certified-pre-owned">
      <Flex flexDirection="column" gap={spacing.defaultSmall}>
        <VipSectionTitle title={t('title')} />
        {isPartnerRecognized && (
          <CPOProgramLogo alt={partnerId} src={`/next-assets/images/cpo/${partnerId}.webp`} />
        )}
        <BodyText color={colors.grey.primary}>
          {t(`${isPartnerRecognized ? partnerId : 'default'}.overview`)}
        </BodyText>
        <DetailList>
          {cpoProgramDetailKeys[partnerId]?.map((detailKey) => (
            <ListItem variant="icon" icon={<CheckmarkAltIcon />} key={detailKey}>
              {t(detailKey)}
            </ListItem>
          ))}
        </DetailList>
      </Flex>
    </Spacing>
  )
}
